import { Component, ElementRef, EventEmitter, HostListener, NgZone, Output, Renderer2, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { BaseComponent, ResponseService, WindowRef } from '@portals/core';
import { AWSUploadService, Channel, CollectibleType, Notification, ThemeType } from '@portals/api';
import { CollectiblesState } from '@portals/store';
import { DeviceDetectorService } from 'ngx-device-detector';
import { HotToastService } from '@ngneat/hot-toast';
import { Location } from '@angular/common';
import { CookieService } from 'ngx-cookie';
import { v4 as uuidv4 } from 'uuid';
import * as moment from 'moment/moment';
import { AnimationOptions } from 'ngx-lottie';
import { AnimationItem } from 'lottie-web';
import { finalize } from 'rxjs';
import { RESPONSE_TYPES, SCREENS } from '@portals/utils';

@Component({
	selector: 'portals-notifications-response',
	templateUrl: './notifications-response.component.html',
	styleUrls: ['./notifications-response.component.scss'],
})
export class NotificationsResponseComponent extends BaseComponent {
	notification = this.responseService.currentPage?.parameters?.notification as Notification;
	notificationType = this.responseService.currentPage?.parameters?.notificationType as RESPONSE_TYPES;
	channel = this.responseService.currentPage?.parameters?.channel as Channel;
	theme?: ThemeType;
	/* UI Elements */
	protected appContainerRef!: ElementRef; // The entire app container
	protected videoUIRef!: ElementRef; // The video being shown on screen
	protected mobileViewRef!: ElementRef; // The mobile view container
	protected mobileDialogViewRef!: ElementRef; // The dialog content container
	protected modalActionRef!: ElementRef; // the modal action view
	protected ctaRef!: ElementRef; // the video action view
	protected responseInputRef!: ElementRef;
	protected maxHeight: string = '';
	protected ctaWidth: string = '';
	private mediaRecorder!: MediaRecorder;
	private recordedChunks: Blob[] = [];

	/* Component variables */
	protected locationCoords: GeolocationCoordinates | undefined;

	// @ts-ignore
	@ViewChild('appContainer', { static: true }) set appContainer(content: ElementRef) {
		if (content) {
			// initially setter gets called with undefined
			this.appContainerRef = content;
		}
	}
	// @ts-ignore
	@ViewChild('displayedVideo', { static: false }) set displayedVideo(content: ElementRef) {
		if (content) {
			// initially setter gets called with undefined
			this.videoUIRef = content;
		}
	}
	// @ts-ignore
	@ViewChild('mobileView', { static: true }) set mobileView(content: ElementRef) {
		if (content) {
			// initially setter gets called with undefined
			this.mobileViewRef = content;
		}
	}

	// @ts-ignore
	@ViewChild('mobileDialogView', { static: true }) set mobileDialogView(content: ElementRef) {
		if (content) {
			// initially setter gets called with undefined
			this.mobileDialogViewRef = content;
		}
	}

	// @ts-ignore
	@ViewChild('ctaView', { static: true }) set ctaView(content: ElementRef) {
		if (content) {
			// initially setter gets called with undefined
			this.ctaRef = content;
		}
	}

	// @ts-ignore
	@ViewChild('responseInput', { static: true }) set responseInput(content: ElementRef) {
		if (content) {
			this.responseInputRef = content;
		}
	}

	/* UI Variables */
	protected activeScreen: RESPONSE_TYPES;
	protected isDesktop: boolean;
	protected isMobile: boolean;
	protected isTablet: boolean;
	protected isIOS: boolean;
	protected audioMode: boolean;
	protected isLandscape: boolean;
	protected startAnimation = false;
	protected finishedResponding = false;
	protected typeReady = false;
	protected startTyping = false;
	protected typingMode = false;
	protected isRecording = false;
	protected setViewComplete = false;
	protected isStopped = false;
	protected hyperLinkRef: string = '';
	protected didClickRecord = false;
	protected animationItem!: AnimationItem;
	protected options: AnimationOptions = {
		path: 'assets/images/lottie/lottie-record-noLoop.json',
	};
	protected audioPlayingOptions: AnimationOptions = {
		path: 'assets/images/lottie/audio-record.json',
		autoplay: true,
		loop: true,
	};
	protected audioOptions: AnimationOptions = {
		path: 'assets/images/lottie/audio-record.json',
		autoplay: false,
		loop: false,
	};
	protected cursorOptions: AnimationOptions = {
		path: 'assets/images/lottie/blink_coursor.json',
		autoplay: true,
		loop: true,
	};

	/* Stream related variables */
	protected response: string = '';
	protected mediaConstraints: MediaStreamConstraints | any;
	protected mediaStream!: MediaStream;
	protected isMediaSetUp = false;
	protected videoFacingMode = 'user';
	protected userRTCConfig = { iceServers: [] };
	protected currentPeerConnection!: RTCPeerConnection;
	protected wowzaWebSocket!: WebSocket;
	protected streamInfo = {
		streamName: '',
		applicationName: 'jotto',
	};

	constructor(protected ngZone: NgZone, protected toast: HotToastService, protected location: Location, protected router: Router, protected cookies: CookieService, protected awsUpload: AWSUploadService, protected collectibleState: CollectiblesState, protected renderer: Renderer2, protected deviceService: DeviceDetectorService, protected window: WindowRef, protected responseService: ResponseService) {
		super();

		/* Set states for ui */
		this.activeScreen = this.notification?.response_type || this.notificationType;
		this.isDesktop = this.deviceService.isDesktop();
		this.isMobile = this.deviceService.isMobile();
		this.isTablet = this.deviceService.isTablet();
		this.isIOS = this.deviceService.device == 'iPhone';
		this.audioMode = false;
		this.isLandscape = window.nativeWindow.innerWidth > window.nativeWindow.innerHeight;
		this.hyperLinkRef = this.deviceService.getDeviceInfo().os == 'iOS' ? 'https://apps.apple.com/us/app/jotto-video/id1446288804' : 'https://play.google.com/store/apps/details?id=us.quadrant2.jotto&hl=en_US&gl=US&pli=1';
		const windowHeight = this.isMobile ? window.nativeWindow.innerHeight : 600;
		const windowWidth = this.isMobile ? window.nativeWindow.innerWidth : 370;
		const maxHeight = `${windowHeight - 304}px`;
		const ctaWidth = `${windowWidth - 106}px`;
		this.maxHeight = maxHeight;
		this.ctaWidth = ctaWidth;
		this.theme = this.channel.links?.theme;
	}

	// ngOnInit(): void {
	// 	this.activeScreen = this.notification.response_type;
	// }

	animationCreated(animationItem: AnimationItem): void {
		this.animationItem = animationItem;
	}

	initView(): void {
		/* Get location and set it */
		navigator.geolocation.getCurrentPosition(
			(locationData: GeolocationPosition) =>
				this.ngZone.run(() => {
					this.locationCoords = locationData.coords;
				}),
			function () {
				// console.log('LOCATION NOT ALLOWED');
			},
			{ timeout: 10000, enableHighAccuracy: true }
		);

		this.setView();

		if (this.activeScreen != RESPONSE_TYPES.TEXT) {
			/* Get user permissions for audio and video */
			navigator.mediaDevices
				.getUserMedia(this.mediaConstraints)
				.then((mediaStream: MediaStream) => {
					this.setProperties(mediaStream);
					setTimeout(() => {
						this.setViewComplete = true;
					}, 1000);
				})
				.catch((e) => {
					// alert("ERROR! PERMISSIONS NOT GRANTED")
					// if (this.activeScreen !== "TEXT") alert("ERROR! PERMISSIONS NOT GRANTED")
				});

			/* Listen for network issues */
			window.addEventListener('offline', () => {
				this.userDisconnected(); // Call your function when the user goes offline
			});
		}
	}

	/* Logic for user disconnecting while streaming */
	userDisconnected() {
		// /* If use is recording then: */
		// if (this.isRecording)
		// 	// This is where you can define the action to be taken when the user disconnects
		// 	// console.log('User is offline. Add your logic here.');
		// // You can trigger other actions, display messages, etc.
	}

	setView() {
		const portrait = window.matchMedia('(orientation: portrait)');

		if (this.isMobile) {
			portrait.matches ? null : this.renderer.setStyle(this.mobileViewRef.nativeElement, 'max-width', '100%');
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'border-radius', '0px');
			// this.renderer.setStyle(this.mobileDialogViewRef.nativeElement, 'max-height', '100vh');
			this.renderer.setStyle(this.appContainerRef.nativeElement, 'height', '100vh');
			this.renderer.setStyle(this.mobileDialogViewRef.nativeElement, 'max-height', window.innerHeight + 'px');
			this.renderer.setStyle(this.mobileDialogViewRef.nativeElement, 'width', '100%');
			this.renderer.setStyle(this.appContainerRef.nativeElement, 'width', '100%');
			if (this.deviceService.device == 'iPhone' && this.deviceService.browser == 'Chrome') {
				this.renderer.setStyle(this.mobileViewRef.nativeElement, 'padding-bottom', '10px');
			}
		}

		// if (this.isMobile) {
		// 	portrait.matches ? null : this.renderer.setStyle(this.mobileViewRef.nativeElement, 'max-width', '100%');
		// 	this.renderer.setStyle(this.channelContentRef.nativeElement, 'padding', '0px 28px 0px');
		// 	// this.renderer.setStyle(this.channelContentRef.nativeElement, 'max-height', this.maxHeight);
		// 	this.renderer.setStyle(this.appContainerRef.nativeElement, 'background', 'var(--portals-theme-primary)');
		// 	this.renderer.setStyle(this.mobileViewRef.nativeElement, 'padding', '20px 0p');
		// }

		if (this.isTablet) {
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'border-radius', '24px');
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'min-height', '650px');
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'max-height', '650px');
			this.renderer.setStyle(this.mobileDialogViewRef.nativeElement, 'max-height', '650px');
			this.renderer.setStyle(this.appContainerRef.nativeElement, 'width', '100%');
		}

		if (this.isDesktop) {
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'border-radius', '24px');
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'min-height', '650px');
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'max-height', '650px');
			this.renderer.setStyle(this.mobileDialogViewRef.nativeElement, 'min-height', '650px');
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'min-width', '370px');
			if (this.videoUIRef && this.videoUIRef.nativeElement.localName !== 'div') {
				this.renderer.setStyle(this.videoUIRef.nativeElement, 'object-fit', this.deviceService.getDeviceInfo().os == 'iOS' ? 'fill' : 'cover');
				this.renderer.setStyle(this.videoUIRef.nativeElement, 'transform', 'rotateY(180deg)');
			}
		}
	}
	getBackgroundColorClassParent() {
		if (this.theme && this.theme.background_color) {
			return { background: this.theme.background_color };
		} else {
			return { background: '#491176' };
		}
	}
	/* Set properties for the media devices, ui states, and deviceIDs */
	setProperties(mediaStream: MediaStream): void {
		// if(this.activeScreen == 'TEXT'){return;}
		/* Clear any previous open tracks since on some android devices, it is necessary to stop the previous track */
		if (this.videoUIRef.nativeElement.srcObject && this.videoUIRef.nativeElement.localName !== 'div') {
			this.videoUIRef.nativeElement.srcObject.getTracks().forEach((track: any) => track.stop());
		}

		/* Set mediaStream state and the videoUIRef's src value to display the screen to user. */
		this.videoUIRef.nativeElement.srcObject = mediaStream;

		// Set up the MediaRecorder with the video stream
		this.mediaRecorder = new MediaRecorder(mediaStream);

		// Handle data available event
		this.mediaRecorder.ondataavailable = (event: any) => {
			if (event.data.size > 0) {
				this.recordedChunks.push(event.data);
			}
		};

		/* If the facing mode is the user we need to mirror the video element - otherwise reset the rotation */
		if (this.videoUIRef.nativeElement.localName !== 'div') {
			if (this.videoFacingMode === 'user') {
				this.renderer.setStyle(this.videoUIRef.nativeElement, 'transform', 'rotateY(180deg)'); // Base transformation
				this.renderer.setStyle(this.videoUIRef.nativeElement, '-webkit-transform', 'rotateY(180deg)'); // Safari and Chrome
				this.renderer.setStyle(this.videoUIRef.nativeElement, '-moz-transform', 'rotateY(180deg)'); // Firefox
			} else {
				this.renderer.setStyle(this.videoUIRef.nativeElement, 'transform', 'rotateY(0deg)'); // Base transformation
				this.renderer.setStyle(this.videoUIRef.nativeElement, '-webkit-transform', 'rotateY(0deg)'); // Safari and Chrome
				this.renderer.setStyle(this.videoUIRef.nativeElement, '-moz-transform', 'rotateY(0deg)'); // Firefox
			}

			this.zoomCameraForLandscape();
		}

		/* Set up constraints with proper deviceIDs */
		this.mediaStream?.getTracks().forEach((track: MediaStreamTrack) => {
			/* If track is of type video, then set it to the video constraint and vice versa. */
			if (track.kind === 'video') {
				this.mediaConstraints.video.deviceId = track.id;
			} else if (track.kind === 'audio') {
				this.mediaConstraints.audio.deviceId = track.id;
			}
		});

		// Handle recording stopped event
		this.mediaRecorder.addEventListener('stop', async () => {
			this.videoUIRef.nativeElement.srcObject.getTracks().forEach((track: any) => track.stop());
			if (this.isMediaSetUp && this.isStopped) {
				const webmBlob = new Blob(this.recordedChunks, {
					type: 'video/webm',
				});
				await this.awsUpload.uploadVideoToS3(webmBlob, `${this.streamInfo.streamName}.webm`).then((data) => {
					if (data) {
						// Notify the service that the upload is complete
						this.awsUpload.notifyUploadComplete(true);
					}
				});
			} else {
				this.isStopped = false;
			}
		});

		/* The media setup is complete! */
		this.isMediaSetUp = true;
	}

	/* Handles Camera Swapping */
	switchCameraView() {
		/* Change media constraints to user or environment based on previous state */
		this.isMediaSetUp = false;
		if (this.videoFacingMode == 'user') this.mediaConstraints.video.facingMode = this.videoFacingMode = 'environment';
		else this.mediaConstraints.video.facingMode = this.videoFacingMode = 'user';
		/* Reset properties */
		navigator.mediaDevices
			.getUserMedia(this.mediaConstraints)
			.then((mediaStream: MediaStream) => {
				this.setProperties(mediaStream);
			})
			.catch((e) => {
				alert('ERROR! PERMISSIONS NOT GRANTED');
			});
	}

	/* Handles Orientation Changes */
	@HostListener('window:orientationchange', ['$event'])
	// @ts-ignore
	handleOrientationChange(event: any) {
		/* Set Proper Dimensions and Set Landscape Mode */
		this.isLandscape = !this.isLandscape;
		if (!this.didClickRecord) {
			/* Set Proper Dimensions and Set Landscape Mode */
			this.mediaConstraints.video.height = this.isLandscape ? (this.deviceService.isDesktop() ? 370 : 1080) : this.deviceService.isDesktop() ? 650 : 1080;

			this.mediaConstraints.video.width = this.isLandscape ? (this.deviceService.isDesktop() ? 650 : 1920) : this.deviceService.isDesktop() ? 370 : 1920;

			/* Reset properties */
			navigator.mediaDevices
				.getUserMedia(this.mediaConstraints)
				.then((mediaStream: MediaStream) => {
					this.setProperties(mediaStream);
				})
				.catch((e) => {
					alert('ERROR! PERMISSIONS NOT GRANTED');
				});
		} else this.zoomCameraForLandscape();
	}

	/* Start the streaming procedure */
	async startStreaming() {
		if (this.mediaRecorder) {
			this.startAnimation = true;
			this.recordedChunks = [];
			this.didClickRecord = true;
			this.mediaRecorder.start();
			// Delay for Animation
			setTimeout(() => {
				this.isRecording = true;
			}, 100);
		}
	}

	/* Stop streaming function */
	async stopStreaming() {
		this.responseService.navigateTo(SCREENS.UPLOADING_SCREEN, {
			channel: this.channel,
			notification: this.notification,
		});

		/* Set new stream name */
		this.streamInfo.streamName = uuidv4();
		this.isStopped = true;

		/* Create collectible once everything is completed */
		const currDate = moment();
		const incident_date = currDate.format('MM-DD-YYYY');
		const incident_time = currDate.format('HH:mm:ss');
		const collectible = {
			data: {
				channel: this.channel.id,
				uuid: this.streamInfo.streamName,
				video: `${this.streamInfo.streamName}.mp4`,
				type: 'stream',
				data: {
					incident_date,
					incident_time,
				},
				status: 'SENT',
				audioOnly: this.audioMode,
				uploaded: true,
				timestamp: Date.now(),
				notification: this.notification?.id,
				project: this.channel.project?.id || this.notification?.project?.id,
				location: {
					latitude: this.locationCoords?.latitude,
					longitude: this.locationCoords?.longitude,
				},
			},
		};
		this.collectibleState.create(collectible).subscribe();

		this.ngZone.runOutsideAngular(() => {
			// console.log('Stopping Lottie')
			this.startAnimation = false;
			this.animationItem?.stop();
			// this.finishedResponding = true
		});

		this.mediaRecorder.stop();
		this.isRecording = false;
		await localStorage.setItem(`notification-${this.notification.id}`, 'true');
	}

	/* Handle text collectible */
	async createTextCollectible() {
		/* Set new stream name */
		this.streamInfo.streamName = uuidv4();
		/* Create text collectible */
		const currDate = moment();
		const incident_date = currDate.format('MM-DD-YYYY');
		const incident_time = currDate.format('HH:mm:ss');
		const collectible = {
			data: {
				channel: this.channel.id,
				uuid: this.streamInfo.streamName,
				data: {
					incident_date,
					incident_time,
				},
				status: 'SENT',
				uploaded: true,
				timestamp: Date.now(),
				notification: this.notification?.id,
				project: this.channel.project?.id || this.notification?.project?.id,
				textResponse: this.response,
				has_transcript_in_bucket: true,
				transcript_gist_set: true,
				type: CollectibleType.text,
				location: {
					latitude: this.locationCoords?.latitude,
					longitude: this.locationCoords?.longitude,
				},
			},
		};

		setTimeout(() => {
			this.subscribe(
				this.collectibleState.create(collectible).pipe(
					finalize(() => {
						localStorage.setItem(`notification-${this.notification.id}`, 'true');
						if (this.notification.external_url && this.notification.shouldRedirect) {
							if (this.deviceService.isDesktop()) {
								const newTab = window.open(this.notification.external_url, '_blank');
								newTab?.focus();
							} else {
								window.open(this.notification.external_url, '_self');
							}
						}
						this.responseService.navigateTo(SCREENS.CHANNEL_FEED_SCREEN, {
							channel: this.channel,
							notification: this.notification,
						});
					})
				)
			);
		}, 3000);

		this.responseService.navigateTo(SCREENS.UPLOADING_SCREEN, {
			channel: this.channel,
			notification: this.notification,
		});
	}

	switchTypingMode() {
		this.typingMode = !this.typingMode;
		// setTimeout(() => {
		this.startTyping = !this.startTyping;
		setTimeout(() => {
			this.typeReady = !this.typeReady;
			this.responseInputRef.nativeElement.click();
			this.responseInputRef.nativeElement.focus();
		}, 10); // Initial amount of 100
		// }, 10); // Initial amount of 500

		// console.log('Start typing >>>>>', this.typingMode, this.typeReady);
	}

	resetTypingMode() {
		this.response = '';
		this.startTyping = false;
		this.typingMode = false;
		this.typeReady = false;
	}

	zoomCameraForLandscape() {
		if (!this.deviceService.isDesktop()) {
			if (this.isLandscape) {
				this.renderer.setStyle(this.videoUIRef.nativeElement, 'height', '120vh');
				this.renderer.setStyle(this.videoUIRef.nativeElement, 'width', '100vw');
			} else {
				this.renderer.setStyle(this.videoUIRef.nativeElement, 'height', '100vh');
				this.renderer.setStyle(this.videoUIRef.nativeElement, 'width', '100vw');
			}
		}
	}

	changeScreen(screen: RESPONSE_TYPES) {
		this.activeScreen = screen;
		this.responseService.currentPage!.parameters.notificationType = screen;
	}

	back() {
		this.responseService.navigateBack();
	}

	protected readonly close = close;
	protected readonly RESPONSE_TYPES = RESPONSE_TYPES;
}
